import { API_ENDPOINTS } from '../config/api'

export type RemediationSeverity = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW'
export type RemediationStatus =
  | 'OPEN'
  | 'IN_PROGRESS'
  | 'BLOCKED'
  | 'READY_FOR_REVIEW'
  | 'VERIFIED'
  | 'CLOSED'

export type RemediationSourceRef = {
  type?: string
  label?: string
  sessionId?: string
  controlKey?: string
  findingId?: string
  auditId?: string
  riskId?: string
  recommendationId?: string
  taskId?: string
}

export type UnifiedRemediationItem = {
  id: string
  severity: RemediationSeverity
  status: RemediationStatus
  title: string
  description?: string | null
  sources: RemediationSourceRef[]
  linkedControls: Array<{ controlCode?: string; standardCode?: string; controlTitle?: string }>
  linkedSystems: Array<{ informationSystemId?: string; id?: string; name?: string }>
  linkedPolicies: Array<{ id?: string; title?: string; code?: string }>
  linkedEvidence: Array<{ id?: string; title?: string }>
  linkedFindings: Array<{ id?: string; title?: string; severity?: string }>
  recommendations: string[]
  requiredEvidence: string[]
  owner?: string | null
  dueDate?: string | null
  verificationState?: string
  remediationState?: string
  priorityScore?: number
  isOverdue?: boolean
  overdueDays?: number
  blockedReason?: string | null
  standardCode?: string | null
  controlKey?: string | null
}

export type UnifiedRemediationMetrics = {
  criticalOpen: number
  overdue: number
  blocked: number
  velocity: number
  openFindings: number
  openRecommendations: number
  totalOpen?: number
  totalItems?: number
}

export type UnifiedRemediationAuthorityPayload = {
  authorityVersion: string
  organizationId: string
  standardCode?: string | null
  generatedAt: string
  metrics: UnifiedRemediationMetrics
  remediationItems: UnifiedRemediationItem[]
  diagnostics?: Record<string, unknown>
}

export async function fetchUnifiedRemediationRegistry(
  token: string,
  standardCode?: string | null,
): Promise<UnifiedRemediationAuthorityPayload> {
  const params = new URLSearchParams()
  if (standardCode && standardCode !== 'ALL') {
    params.set('standardCode', standardCode)
  }
  const qs = params.toString()
  const url = qs ? `${API_ENDPOINTS.REMEDIATION_REGISTRY}?${qs}` : API_ENDPOINTS.REMEDIATION_REGISTRY
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } })
  const json = await res.json().catch(() => ({}))
  if (!res.ok) {
    throw new Error(json?.message || 'Failed to load remediation registry')
  }
  return json?.data as UnifiedRemediationAuthorityPayload
}

export async function fetchRemediationDiagnostics(
  token: string,
  standardCode?: string | null,
): Promise<Record<string, unknown>> {
  const params = new URLSearchParams()
  if (standardCode && standardCode !== 'ALL') {
    params.set('standardCode', standardCode)
  }
  const qs = params.toString()
  const url = qs ? `${API_ENDPOINTS.REMEDIATION_DIAGNOSTICS}?${qs}` : API_ENDPOINTS.REMEDIATION_DIAGNOSTICS
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } })
  const json = await res.json().catch(() => ({}))
  if (!res.ok) {
    throw new Error(json?.message || 'Failed to load remediation diagnostics')
  }
  return (json?.data || {}) as Record<string, unknown>
}
